/**
 * Segment types for the frontend.
 * A segment is a named boolean expression over clusters.
 */

// ========================================
// Segment Expression Types
// ========================================

export interface SegmentClusterCondition {
  type: 'cluster';
  clusterId: number;
  /** If true, matches units NOT in this cluster */
  negate: boolean;
}

export interface SegmentExpressionGroup {
  type: 'group';
  logicalOperator: 'AND' | 'OR';
  conditions: (SegmentClusterCondition | SegmentExpressionGroup)[];
}

export type SegmentExpression = SegmentExpressionGroup;

// Type guards
export function isSegmentClusterCondition(
  condition: SegmentClusterCondition | SegmentExpressionGroup,
): condition is SegmentClusterCondition {
  return condition.type === 'cluster';
}

export function isSegmentExpressionGroup(
  condition: SegmentClusterCondition | SegmentExpressionGroup,
): condition is SegmentExpressionGroup {
  return condition.type === 'group';
}

// ========================================
// Segment Entity
// ========================================

export interface Segment {
  id: number;
  name: string;
  description?: string;
  expression: SegmentExpression;
  labels: string[];
  active: boolean;
  unitCount?: number;
  clusterNames?: string[];
  createdAt: string;
  updatedAt: string;
}

// ========================================
// DTOs
// ========================================

export interface CreateSegmentDto {
  name: string;
  description?: string;
  expression: SegmentExpression;
  labels?: string[];
  active?: boolean;
}

export interface UpdateSegmentDto {
  name?: string;
  description?: string;
  expression?: SegmentExpression;
  labels?: string[];
  active?: boolean;
}

// ========================================
// API Response Types
// ========================================

export interface SegmentUnit {
  id: string;
  boxNumber?: string;
  referencePlus?: string;
  floor?: number;
  unitType?: string;
  unitSubtype?: string;
  grossSurface?: number;
  netSurface?: number;
  building?: {
    id: string;
    googlePlaceLongAddress?: string;
    company?: {
      id: number;
      name: string;
    };
  };
}

export interface SegmentEvaluationResult {
  segment: Segment;
  units: SegmentUnit[];
  unitCount: number;
}

// ========================================
// Predefined Labels
// ========================================

export const SEGMENT_LABELS = [
  'onderhoud',
  'juridisch',
  'regelgeving',
  'financieel',
  'verhuur',
  'verzekering',
  'energie',
  'veiligheid',
] as const;

export type SegmentLabel = (typeof SEGMENT_LABELS)[number];

/**
 * Label display configuration (Dutch labels)
 */
export const SEGMENT_LABEL_CONFIG: Record<string, { label: string; color: string }> = {
  onderhoud: { label: 'Onderhoud', color: 'bg-blue-100 text-blue-800' },
  juridisch: { label: 'Juridisch', color: 'bg-purple-100 text-purple-800' },
  regelgeving: { label: 'Regelgeving', color: 'bg-amber-100 text-amber-800' },
  financieel: { label: 'Financieel', color: 'bg-green-100 text-green-800' },
  verhuur: { label: 'Verhuur', color: 'bg-teal-100 text-teal-800' },
  verzekering: { label: 'Verzekering', color: 'bg-orange-100 text-orange-800' },
  energie: { label: 'Energie', color: 'bg-yellow-100 text-yellow-800' },
  veiligheid: { label: 'Veiligheid', color: 'bg-red-100 text-red-800' },
};
